import * as React from "react";
import { toast } from "sonner";
import { Send, X, MessagesSquare } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import DetailDrawer from "@/components/DetailDrawer";
import EntityAvatar from "@/components/identity/EntityAvatar";

const MAX_BODY = 4000;

function fmtTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

/**
 * ThreadDrawer — opens on the right side, shows a message + all its
 * child replies, and lets you reply in the thread. "Also send to
 * channel" is a checkbox on the composer inside the drawer.
 */
export default function ThreadDrawer({ open, onOpenChange, companyId, conversation, rootId }) {
  const [root, setRoot] = React.useState(null);
  const [replies, setReplies] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [body, setBody] = React.useState("");
  const [alsoChannel, setAlsoChannel] = React.useState(false);
  const [sending, setSending] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!open || !rootId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/companies/${companyId}/comms/conversations/${conversation.id}/threads/${rootId}/messages`,
      );
      setRoot(data.root);
      setReplies(data.replies || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load thread");
    } finally { setLoading(false); }
  }, [companyId, conversation?.id, rootId, open]);

  React.useEffect(() => { load(); }, [load]);

  // Listen to SSE thread events
  React.useEffect(() => {
    if (!open) return () => {};
    const onEvt = (e) => {
      const evt = e.detail;
      if (!evt || evt.module !== "comms") return;
      const p = evt.payload || {};
      if (evt.type === "message.thread_reply" && p.thread_root === rootId) {
        setReplies((prev) => (prev.some((r) => r.id === p.message?.id) ? prev
          : [...prev, p.message]));
      }
    };
    window.addEventListener("fable:event", onEvt);
    return () => window.removeEventListener("fable:event", onEvt);
  }, [open, rootId]);

  async function send() {
    const val = body.trim();
    if (!val || sending) return;
    setSending(true);
    try {
      await api.post(
        `/companies/${companyId}/comms/conversations/${conversation.id}/threads/${rootId}/messages`,
        { body: val, also_send_to_channel: alsoChannel },
      );
      setBody(""); setAlsoChannel(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't reply");
    } finally { setSending(false); }
  }

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Thread"
      subtitle={conversation?.slug ? `in #${conversation.slug}` : "in a direct message"}
      testid="comms-thread-drawer"
    >
      {loading || !root ? (
        <div className="p-6 text-[12.5px] text-brand-muted" data-testid="thread-loading">Loading thread…</div>
      ) : (
        <div className="flex flex-col h-full">
          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
            <ThreadMessage msg={root} muted />
            <div className="text-[10.5px] tracking-[0.16em] uppercase text-brand-muted flex items-center gap-2">
              <MessagesSquare size={12} /> {replies.length} repl{replies.length === 1 ? "y" : "ies"}
            </div>
            {replies.map((m) => <ThreadMessage key={m.id} msg={m} />)}
          </div>
          <div className="border-t border-brand-rule bg-card px-4 py-3">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Reply in this thread…"
              className="field-input py-2 min-h-[46px] max-h-[180px] resize-y w-full text-[13px]"
              maxLength={MAX_BODY + 20}
              data-testid="thread-reply-input"
            />
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {conversation?.kind !== "dm" && (
                <label className="inline-flex items-center gap-1.5 text-[11.5px] text-brand-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={alsoChannel}
                    onChange={(e) => setAlsoChannel(e.target.checked)}
                    data-testid="thread-also-channel"
                  />
                  Also send to channel
                </label>
              )}
              <div className="flex-1" />
              <button
                type="button"
                onClick={send}
                disabled={sending || !body.trim()}
                className="btn-violet h-8"
                data-testid="thread-reply-send"
              >
                {sending ? "…" : <>Reply <Send size={12} /></>}
              </button>
            </div>
          </div>
        </div>
      )}
    </DetailDrawer>
  );
}

function ThreadMessage({ msg, muted = false }) {
  return (
    <div className={`flex gap-3 ${muted ? "opacity-90" : ""}`} data-testid={`thread-msg-${msg.id}`}>
      <EntityAvatar
        kind="person" name={msg.sender_name || "?"} id={msg.sender_id || msg.sender_name}
        size={24} className="shrink-0"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <div className="text-[12.5px] font-semibold text-brand-ink truncate">{msg.sender_name}</div>
          <div className="mono-num text-[10.5px] text-brand-muted">{fmtTime(msg.created_at)}</div>
        </div>
        <div className="text-[13px] text-brand-ink whitespace-pre-wrap break-words mt-0.5">
          {msg.deleted ? <span className="italic text-brand-faint">[deleted]</span> : msg.body}
        </div>
      </div>
    </div>
  );
}
